/**
 * The Outbox's "next step" of a lead (PREC-4372): which of its schedule rows goes out next, and
 * when. The API sends a lead's schedule rows and no next-step field, so the rule lives here once,
 * read by the web's Outbox card (its send time, `getPendingScheduleDate` in
 * `apps/web/src/pages/Private/pages/Messages/pages/Outbox/outbox-category.helper.ts`) and by the
 * MCP `get_outbox` tool (`apps/api/src/mcp/tools/outbox-lead.util.ts`).
 *
 * The next step is the earliest step that still waits for a send time of its own: not done (sent
 * or cancelled), not cancelled, not processed, switched on (a paused row keeps its time but sends
 * nothing), not a campaign step whose own campaign does not run (the publisher sends a campaign step
 * only while its campaign is RUNNING, `CAMPAIGN_RUNNING_EXISTS_SQL`; a manual message goes out
 * whatever its thread's campaign does), and not the LinkedIn invite-acceptance gate, which never
 * sends anything — it only watches for 30 days whether the invitation is accepted. A row with no send time has no place in
 * the order: a follow-up still parked behind an invitation gets its time on acceptance, and a row
 * that fell due is handed to the sender with its time cleared.
 */

/**
 * The LinkedIn invite-acceptance gate's step id. The one source: the API's
 * `LINKEDIN_INVITE_CONDITIONAL_STEP_ID` is this constant.
 */
export const INVITE_ACCEPTANCE_GATE_STEP_ID = "n99-cond";

/**
 * The step id of a message the user wrote in the app, not a campaign step. The one source: the
 * API's `MANUAL_FOLLOW_UP_STEP_ID` is this constant.
 */
export const MANUAL_MESSAGE_STEP_ID = "n99";

/** The columns of a schedule row the rule reads; dates arrive as ISO strings or as `Date`s. */
export interface OutboxNextStepCandidate {
	stepId?: string | null;
	scheduledFor?: string | Date | null;
	active?: boolean | null;
	processingState?: string | null;
	canceledAt?: string | Date | null;
	processedAt?: string | Date | null;
	/** The step's own campaign (`schedule.campaignId`, a campaign uuid of the list). */
	campaignId?: string | null;
	/**
	 * The run status of the step's own campaign (`campaign_status`): null when the campaign has no
	 * status row (it was never started), absent when the row was read without it.
	 */
	campaignStatus?: string | null;
}

/** Why a campaign step's own campaign holds it: the campaign is not running. */
export type CampaignHold = "paused" | "not started" | "ended" | "not running";

const HOLD_BY_STATUS: Record<string, CampaignHold> = {
	PAUSED: "paused",
	DRAFT: "not started",
	LOADING: "not started",
	ENDED: "ended",
};

/**
 * Why the step's own campaign holds it, or null when nothing does. The publisher sends a campaign
 * step only while its campaign is RUNNING, so every other status holds it, and so does a campaign
 * with no status row at all (never started). A manual message and the invite-acceptance gate are
 * never held: the publisher takes them whatever their thread's campaign does. A row read without
 * its campaign's status (`campaignStatus` absent) is not held — there is nothing to decide on.
 */
export function campaignHoldOf(step: OutboxNextStepCandidate): CampaignHold | null {
	if (
		step.campaignStatus === undefined ||
		step.campaignStatus === "RUNNING" ||
		step.stepId === MANUAL_MESSAGE_STEP_ID ||
		step.stepId === INVITE_ACCEPTANCE_GATE_STEP_ID
	) {
		return null;
	}

	if (step.campaignStatus === null) {
		return step.campaignId ? "not started" : null;
	}

	return HOLD_BY_STATUS[step.campaignStatus] ?? "not running";
}

const timeOf = (value: string | Date): number => new Date(value).getTime();

/** Whether a row still waits for its own send time and will go out at it. */
export function isWaitingOutboxStep(step: OutboxNextStepCandidate | null | undefined): boolean {
	return (
		!!step &&
		step.processingState !== "done" &&
		!!step.scheduledFor &&
		step.active === true &&
		step.stepId !== INVITE_ACCEPTANCE_GATE_STEP_ID &&
		!step.canceledAt &&
		!step.processedAt &&
		campaignHoldOf(step) === null
	);
}

/**
 * The lead's next step: the waiting row ({@link isWaitingOutboxStep}) with the earliest send time,
 * the first of them in the given order on a tie. Null when nothing waits for a time.
 */
export function findNextOutboxStep<T extends OutboxNextStepCandidate>(
	steps: readonly T[] | null | undefined
): T | null {
	const waiting = (steps ?? []).filter(isWaitingOutboxStep);

	waiting.sort((a, b) => timeOf(a.scheduledFor!) - timeOf(b.scheduledFor!));

	return waiting[0] ?? null;
}
